import { Button } from 'affiny-ui/src';
import React, { memo, useCallback, lazy, Suspense } from 'react';
import styled from 'styled-components';
import { Text, Flex, Box } from '../primitives';
import rem from '../../utils/rem';
import { Fallback } from '../fallback';

const EmptyMessage = lazy(() => import('../svgs/svgr/EmptyMessage'));
const EmptyFavorite = lazy(() => import('../svgs/svgr/EmptyFavorite'));
const EmptyMatchlist = lazy(() => import('../svgs/svgr/EmptyMatchlist'));

type Props = {
  label?: string;
  text?: string;
  subText?: string;
  action?: string;
  onClick?: () => void;
  type?: 'message' | 'favorite' | 'matchlist';
  children?: React.ReactNode;
};

const Empty = ({
  label,
  text,
  subText,
  action,
  onClick,
  type,
  children,
}: Props) => {
  const getSvg = useCallback(() => {
    switch (type) {
      case 'message':
        return <EmptyMessage />;

      case 'favorite':
        return <EmptyFavorite />;

      case 'matchlist':
        return <EmptyMatchlist />;
    }

    return null;
  }, [type]);

  return (
    <Flex
      flexDirection="column"
      height="100%"
      alignItems="center"
      justifyContent="center"
      data-test="goToRateCard-from-empty"
    >
      {label && (
        <Text color="secondary" fontWeight={600}>
          {label}
        </Text>
      )}
      {(type || text || action) && (
        <Flex
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          {type && (
            <Box mb="large">
              <Suspense fallback={<Fallback />}>{getSvg()}</Suspense>
            </Box>
          )}
          {text && (
            <Text
              fontSize={rem(18)}
              lineHeight={rem(24)}
              fontWeight={600}
              textAlign="center"
            >
              {text}
            </Text>
          )}
          {subText && (
            <Text
              fontSize={rem(14)}
              lineHeight={rem(24)}
              textAlign="center"
              mt="large"
            >
              {subText}
            </Text>
          )}
          {action && (
            <SButton
              onClick={onClick}
              text={action}
              size="small"
              variant="outlined"
              color="primary"
            />
          )}
          {children}
        </Flex>
      )}
    </Flex>
  );
};

const SButton = styled(Button)`
  margin-top: ${({ theme }) => theme.space.xLarge};
`;

export default memo(Empty);
